import { apiFetch } from '@/lib/api/client';
import type { AttachmentOut } from '@/lib/api/types';

/**
 * Chat document attachments (spec 0015). Uploads happen the moment a file is
 * selected, before the message is sent: the backend extracts the text, caps
 * per-message and per-session, and returns the (possibly new) conversation id
 * so later uploads and the send reuse the same session.
 */

export interface UploadChatAttachmentInput {
  agentId: string;
  /** Omit to open a fresh conversation; the id comes back in `session_id`. */
  sessionId?: string;
  file: File;
}

/**
 * POST /chat/attachments (multipart). apiFetch sends FormData as-is so the
 * browser sets the multipart boundary (see lib/api/knowledge.ts). Errors throw
 * a typed ApiError (422 unsupported/oversized/over-cap/unextractable, 502
 * storage) whose message is the backend detail string.
 */
export function uploadChatAttachment({
  agentId,
  sessionId,
  file,
}: UploadChatAttachmentInput): Promise<AttachmentOut> {
  const form = new FormData();
  form.append('file', file);
  form.append('agent_id', agentId);
  if (sessionId) form.append('session_id', sessionId);
  return apiFetch<AttachmentOut>('/chat/attachments', { method: 'POST', body: form });
}

/** DELETE /attachments/{id}: drops a not-yet-sent attachment (409 once sent). */
export function deleteChatAttachment(id: string): Promise<void> {
  return apiFetch<void>(`/attachments/${id}`, { method: 'DELETE' });
}
